import { useTranslation } from 'react-i18next';
import { GlobalItems } from '@/accessControl/GlobalItems.atom';
import { VersioningSection } from '@/accessControl/VersioningSection.molecule';
import { Checkbox } from '@/core/Checkbox.atom';
import { isLockingAndVersioningEnabled } from '@/services/systemConfiguration.utilities';
import { useWorkbookIsTopic } from '@/workbook/WorkbookIsTopic.context';
import { Icon } from '@seeqdev/qomponents';
import type { VersionState } from '@/core/hooks/useVersionState.hook';

interface AclModalLockTabProps {
  checkboxHandler: (isChecked: boolean) => void;
  understandLockingIsPermanent: boolean;
  isLocked: boolean;
  lockedTimestamp: string;
  lockedByDisplayName: string | undefined;
  userHasManagePermission: boolean;
  hasLockingFailedPreviouslyDueToGlobalItems: boolean;
  versionState: VersionState;
  /** When set, controls versioning UI; otherwise `isLockingAndVersioningEnabled()` is used. */
  includeVersioningSection?: boolean;
}

export const AclModalLockTab = ({
  checkboxHandler,
  understandLockingIsPermanent,
  isLocked,
  lockedTimestamp,
  lockedByDisplayName,
  userHasManagePermission,
  hasLockingFailedPreviouslyDueToGlobalItems,
  versionState,
  includeVersioningSection,
}: AclModalLockTabProps) => {
  const { t } = useTranslation();
  const isTopic = useWorkbookIsTopic();

  const showVersioningSection = includeVersioningSection ?? isLockingAndVersioningEnabled();
  const unknown = t('UNKNOWN');
  const displayedLockedTimestamp = lockedTimestamp || unknown;
  const displayedLockedBy = lockedByDisplayName || unknown;

  const LockAnalysisModalContent = () => {
    if (userHasManagePermission && !hasLockingFailedPreviouslyDueToGlobalItems) {
      return (
        <>
          <div className="p10">
            <Icon
              icon="fa-exclamation-triangle"
              type="warning"
              testId="show-error-fallback-popover"
              extraClassNames="mr5"
            />
            {isTopic ? t('ACCESS_CONTROL.LOCKING.WARNING_TOPIC') : t('ACCESS_CONTROL.LOCKING.WARNING_1')}
          </div>
          <p>{isTopic ? t('ACCESS_CONTROL.LOCKING.TOPIC_DESCRIPTION') : t('ACCESS_CONTROL.LOCKING.DESCRIPTION')}</p>

          {showVersioningSection && <VersioningSection versionState={versionState} />}

          <div className="mt10">
            <Checkbox
              id="understand-lock-is-permanent-checkbox"
              isChecked={understandLockingIsPermanent}
              onChange={() => checkboxHandler(!understandLockingIsPermanent)}
              label={isTopic ? 'ACCESS_CONTROL.LOCKING.TOPIC_UNDERSTAND' : 'ACCESS_CONTROL.LOCKING.UNDERSTAND'}
            />
          </div>
        </>
      );
    }

    if (!userHasManagePermission) {
      return (
        <>
          {isTopic
            ? t('ACCESS_CONTROL.LOCKING.INSUFFICIENT_PERMISSION_TO_LOCK_TOPIC')
            : t('ACCESS_CONTROL.LOCKING.INSUFFICIENT_PERMISSION_TO_LOCK')}
        </>
      );
    }

    return null;
  };

  const LockAnalysis = (
    <div className="flexRowContainer flexFill mt15 overflowVisible" id="aclGrid">
      {LockAnalysisModalContent()}
      <div className="flexRowContainer justify-center tw:w-full mt5 mb10">
        <GlobalItems hasLockingFailedPreviouslyDueToGlobalItems={hasLockingFailedPreviouslyDueToGlobalItems} />
      </div>
    </div>
  );

  const LockedAnalysisMetadata = (
    <div className="mt50" data-testid="lockSuccessMessage">
      <p>{isTopic ? t('ACCESS_CONTROL.LOCKING.IS_TOPIC_LOCKED') : t('ACCESS_CONTROL.LOCKING.IS_LOCKED')}</p>
      <div>
        <b>{t('ACCESS_CONTROL.LOCKING.LOCKED_ON_DATE')}</b>
        {displayedLockedTimestamp}
      </div>
      <div>
        <b>{t('ACCESS_CONTROL.LOCKING.LOCKED_BY_USER')}</b>
        {displayedLockedBy}
      </div>
    </div>
  );

  return <>{isLocked ? LockedAnalysisMetadata : LockAnalysis}</>;
};
